<template>
  <div>
    <AppHeading class="mb-5">
      <AppTooltip>
        <button
          class="text-dark bg-none border-0 d-inline-flex align-items-center home__term-selector"
          type="button"
          @click="showTermSelectModal = true"
        >
          <span class="font-weight-bold">My Forms</span>
          <span class="ml-2 text-muted font-weight-normal">{{ $store.getters.termDisplayName(viewingTerm) }}</span>
          <AppIcon class="text-muted" name="keyboard_arrow_down" />
        </button>

        <template #tooltip>View a different term...</template>
      </AppTooltip>
    </AppHeading>
    <AppLoading v-if="isLoadingForms || isLoadingTermsAndProfessor" style="height: 60vh" />
    <template v-else>
      <div class="d-flex mb-4">
        <AppButton
          class="btn rounded-15 mr-2 py-1 shadow-none"
          :class="[ status === undefined ? 'btn-raised' : 'bg-light' ]"
          @click="status = undefined"
        >
          All
        </AppButton>
        <AppButton
          class="btn rounded-15 mr-2 py-1 btn-danger shadow-none"
          :class="[ status === 'pending' ? 'btn-raised' : 'bg-light' ]"
          @click="status = 'pending'"
        >
          Pending
        </AppButton>
        <AppButton
          class="btn rounded-15 mr-2 py-1 btn-warning shadow-none"
          :class="[ status === 'incomplete' ? 'btn-raised' : 'bg-light' ]"
          @click="status = 'incomplete'"
        >
          Incomplete
        </AppButton>
        <AppButton
          class="btn rounded-15 mr-2 py-1 btn-success shadow-none"
          :class="[ status === 'complete' ? 'btn-raised' : 'bg-light' ]"
          @click="status = 'complete'"
        >
          Complete
        </AppButton>
      </div>
      <AsyncPagination
        :listLength="formsTotalCount"
        :per="pagination.limit"
        :currentPage="pagination.currentPage"
        class="mx-2"
        @updatePagination="updatePagination"
      />
      <AppTable
        class="mt-5"
        :rows="forms"
        resourceName="Forms"
      >
        <template #header>
          <th class="th">Name</th>
          <th class="th">Status</th>
          <th class="th">Response Last Edited</th>
          <th class="th">Actions</th>
        </template>
        <template #default="{ row: form }">
          <td class="align-middle">{{ form.formData.formTitle }}</td>
          <td class="align-middle">
            <div class="d-flex align-items-center">
              <AppIcon v-if="form.status === 'pending'" name="radio_button_unchecked" :size="16" class="text-danger" />
              <AppIcon v-else-if="form.status === 'incomplete'" name="radio_button_checked" :size="16" class="text-warning" />
              <AppIcon v-else-if="form.status === 'complete'" name="check_circle_outline" :size="16" class="text-success" />
              <span class="ml-2">{{ decamelize(form.status) }}</span>
            </div>
          </td>
          <td class="align-middle">
            <span v-if="form.response">{{ formatDate(form.response.lastEdited) }}</span>
            <span v-else class="text-muted">N/A</span>
          <td>
            <AppButton
              class="bg-light btn-sm"
              @click="goToForm(form)"
            >
              <AppIcon name="edit" :size="16" class="mr-2" />
              {{ form.status === 'complete' ? 'Edit Response': 'Fill Out Form' }}
            </AppButton>
          </td>
        </template>
      </AppTable>
    </template>

    <CourseEditorViewingSemesterModal
      v-if="showTermSelectModal"
      :data="{ viewAll: true }"
      :updateRoute="false"
      @close="showTermSelectModal = false"
      @select="showTermSelectModal = false"
    />
  </div>
</template>

<script>
import moment from 'moment'
import store from '@/store'
import { ref, computed, watch } from '@vue/composition-api'
import useInstructorPreferenceForms from '@/components/composables/useInstructorPreferenceForms'
import CourseEditorViewingSemesterModal from '@sm/client/components/CourseEditor/CourseEditorViewingSemesterModal'
import AsyncPagination from '@/components/AsyncPagination'
import usePagination from '@/components/composables/usePagination'

export default {
  components: {
    CourseEditorViewingSemesterModal,
    AsyncPagination,
  },
  setup (props, { root }) {
    const showTermSelectModal = ref(false)
    const isLoadingTermsAndProfessor = ref(false)
    const viewingTerm = computed(() => store.getters.viewingTerm)
    const instructorId = computed(() => store.getters.userProfessor._id)
    const school = ref(store.getters.school)
    const year = computed(() => store.getters.viewingTerm.year)
    const semester = computed(() => store.getters.viewingTerm.semester)
    const user = ref(store.getters.user)
    const status = ref()

    const { pagination, updatePagination } = usePagination({ root, limit: 20 })

    const {
      isLoadingForms,
      forms,
      formsTotalCount,
      fetchInstructorPreferenceForms,
    } = useInstructorPreferenceForms({
      school,
      instructorId,
      year,
      semester,
      pagination,
      status,
    })

    watch([ viewingTerm, pagination, status ], fetchInstructorPreferenceForms, { immediate: true })

    return {
      // methods
      fetchInstructorPreferenceForms,
      updatePagination,
      // values
      user,
      school,
      year,
      semester,
      isLoadingForms,
      isLoadingTermsAndProfessor,
      showTermSelectModal,
      viewingTerm,
      forms,
      formsTotalCount,
      pagination,
      status,
    }
  },
  async mounted () {
    this.isLoadingTermsAndProfessor = true
    await Promise.all([
      this.$store.dispatch('loadProfessors', {
        school: this.school,
        year: this.year,
        semester: this.semester,
      }),
      this.$store.dispatch('loadTerms', { school: this.school }),
    ])
    this.isLoadingTermsAndProfessor = false
    this.fetchInstructorPreferenceForms()
  },
  methods: {
    formatDate (epoch) {
      return moment(epoch).format('lll')
    },
    goToForm (form) {
      return this.$router.push({
        name: 'PreferenceFormAuthorized',
        params: { id: form._id, school: this.school },
      })
    },
  },
}
</script>

<style scoped>
  .home__term-selector:hover {
    opacity: 0.8;
    cursor: pointer;
  }
</style>
